import { colors, fonts } from '@bridebook/ui';
import type { ValueOf } from '@bridebook/web/lib/types';
import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';
import {
  FontPairs,
  Fonts,
  IFontStyle,
  Palettes,
  TColorPaletteHighlights,
  TFontsPair,
  TWebsiteTheme,
  TWebsiteThemeId,
} from '@bridebook/web/lib/wedding-website/types';
import { getThemeId } from '@bridebook/web/lib/wedding-website/utils/theme';

export const THEME_BRIDEBOOK: TWebsiteTheme = {
  palette: Palettes.Bridebook,
  font: FontPairs.Bridebook,
};

export const THEME_FOLIAGE: TWebsiteTheme = {
  palette: Palettes.Foliage,
  font: FontPairs.Foliage,
};

export const THEME_ROSES: TWebsiteTheme = {
  palette: Palettes.Roses,
  font: FontPairs.Roses,
};

export const THEME_WHIMSICAL: TWebsiteTheme = {
  palette: Palettes.Whimsical,
  font: FontPairs.Whimsical,
};

export const THEMES: TWebsiteTheme[] = [
  THEME_BRIDEBOOK,
  THEME_FOLIAGE,
  THEME_ROSES,
  THEME_WHIMSICAL,
];

// TODO: consider a mapping function capable of preserving object key type
export const THEMES_WITH_IDS = {
  [getThemeId(THEME_BRIDEBOOK)]: THEME_BRIDEBOOK,
  [getThemeId(THEME_FOLIAGE)]: THEME_FOLIAGE,
  [getThemeId(THEME_ROSES)]: THEME_ROSES,
  [getThemeId(THEME_WHIMSICAL)]: THEME_WHIMSICAL,
} as const;

export const THEME_IDS: TWebsiteThemeId[] = THEMES.map(getThemeId);

export const DEFAULT_THEME = THEME_BRIDEBOOK;

export const ASSET_PREFIX = `${IMAGES_BASE_URL}/assets/wedding-websites-imgs`;

export type TWebsiteBackgroundType =
  | 'thumbnail'
  | 'themeCard'
  | 'tileBackground'
  | 'tileBackgroundAlt'
  | 'sectionBackgroundAlt';

export const PALETTE_BACKGROUNDS: Record<Palettes, Record<TWebsiteBackgroundType, string>> = {
  [Palettes.Bridebook]: {
    thumbnail: `${ASSET_PREFIX}/palette-bridebook-thumbnail.png`,
    themeCard: `${ASSET_PREFIX}/palette-bridebook-theme-card.png`,
    tileBackground: `${ASSET_PREFIX}/palette-bridebook-theme-card.png`,
    tileBackgroundAlt: `${ASSET_PREFIX}/palette-bridebook-theme-card-alt-full.png`,
    sectionBackgroundAlt: `${ASSET_PREFIX}/palette-bridebook-theme-card-alt-column.png`,
  },
  [Palettes.Foliage]: {
    thumbnail: `${ASSET_PREFIX}/palette-foliage-thumbnail.png`,
    themeCard: `${ASSET_PREFIX}/palette-foliage-theme-card.png`,
    tileBackground: `${ASSET_PREFIX}/palette-foliage-theme-card.png`,
    tileBackgroundAlt: `${ASSET_PREFIX}/palette-bridebook-theme-card-alt-full.png`,
    sectionBackgroundAlt: `${ASSET_PREFIX}/palette-bridebook-theme-card-alt-column.png`,
  },
  [Palettes.Roses]: {
    thumbnail: `${ASSET_PREFIX}/palette-roses-thumbnail.png`,
    themeCard: `${ASSET_PREFIX}/palette-roses-theme-card.png`,
    tileBackground: `${ASSET_PREFIX}/palette-roses-theme-card.png`,
    tileBackgroundAlt: `${ASSET_PREFIX}/palette-bridebook-theme-card-alt-full.png`,
    sectionBackgroundAlt: `${ASSET_PREFIX}/palette-bridebook-theme-card-alt-column.png`,
  },
  [Palettes.Whimsical]: {
    thumbnail: `${ASSET_PREFIX}/palette-whimsical-thumbnail.png`,
    themeCard: `${ASSET_PREFIX}/palette-whimsical-theme-card.png`,
    tileBackground: `${ASSET_PREFIX}/palette-whimsical-theme-card.png`,
    tileBackgroundAlt: `${ASSET_PREFIX}/palette-bridebook-theme-card-alt-full.png`,
    sectionBackgroundAlt: `${ASSET_PREFIX}/palette-bridebook-theme-card-alt-column.png`,
  },
};

export const PALETTE_EXTRA_ASSETS: Record<Palettes, string> = {
  [Palettes.Bridebook]: `${ASSET_PREFIX}/palette-bridebook-tile-asset.svg`,
  [Palettes.Foliage]: `${ASSET_PREFIX}/palette-foliage-tile-asset.png`,
  [Palettes.Roses]: `${ASSET_PREFIX}/palette-roses-tile-asset.png`,
  [Palettes.Whimsical]: `${ASSET_PREFIX}/palette-whimsical-tile-asset.png`,
};

const COLORS = {
  bleachedLinen: 'rgba(248, 243, 229, 1)',
  // @deprecated this colour will possibly not be used in the final version
  davysGrey: 'rgba(92, 92, 93, 1)',
  etonBlue: 'rgba(151, 183, 163, 1)',
  // @deprecated this colour will possibly not be used in the final version
  graniteGray: 'rgba(98, 99, 93, 1)',
  indigoCrush: colors.indigoCrush,
  indigoCrush04: colors.indigoCrush04,
  indigoCrush10: colors.indigoCrush10,
  // @deprecated this colour will possibly not be used in the final version
  irresistible: 'rgba(176, 71, 104, 1)',
  licorice: 'rgba(40, 13, 21, 1)',
  // @deprecated this colour will possibly not be used in the final version
  linen: 'rgba(248, 238, 229, 1)',
  // @deprecated this colour will possibly not be used in the final version
  periwinkleCrayola: 'rgba(203, 211, 233, 1)',
  // @deprecated this colour will possibly not be used in the final version
  persianPlum: 'rgba(118, 37, 37, 1)',
  pinkJoy: colors.pinkJoy,
  queenPink: 'rgba(239, 212, 218, 1)',
  queenPinkWhimsy: 'rgba(238, 201, 213, 1)',
  solidPink: 'rgba(139, 45, 75, 1)',
  space: colors.space,
  space08: colors.space08,
  space60: colors.space60,
  space75: colors.space75,
  space40: colors.space40,
  teal60: colors.teal60,
  wenge: 'rgba(99, 94, 88, 1)',
  white: colors.white,
  whimsicalBackgroundColor1: 'rgba(220, 146, 169, 1)',
  rosesBackgroundColor1: 'rgba(237, 239, 240, 1)',
} as const;

export type TPaletteColors = {
  text: ValueOf<typeof COLORS>;
  themeCard: ValueOf<typeof COLORS>;
  thumbnail: ValueOf<typeof COLORS>;
  primary: ValueOf<typeof COLORS>;
  secondary: ValueOf<typeof COLORS>;
  tertiary: ValueOf<typeof COLORS>;
  button: ValueOf<typeof COLORS>;
  buttonText: ValueOf<typeof COLORS>;
  background: ValueOf<typeof COLORS>;
  backgroundAlt: ValueOf<typeof COLORS>;
  input: {
    borderColor: ValueOf<typeof COLORS>;
    borderColorActive: ValueOf<typeof COLORS>;
    borderColorHover: ValueOf<typeof COLORS>;
    color: ValueOf<typeof COLORS>;
    labelColor: ValueOf<typeof COLORS>;
    helper: ValueOf<typeof COLORS>;
  };
};

export const BRIDEBOOK_PALETTE_COLORS: TPaletteColors = {
  text: COLORS.space75,
  themeCard: COLORS.space60,
  thumbnail: COLORS.indigoCrush,
  primary: COLORS.teal60,
  secondary: COLORS.indigoCrush10,
  tertiary: COLORS.space75,
  button: COLORS.indigoCrush,
  buttonText: COLORS.white,
  background: COLORS.indigoCrush10,
  backgroundAlt: COLORS.indigoCrush04,
  input: {
    borderColor: COLORS.space40,
    borderColorActive: COLORS.indigoCrush,
    borderColorHover: COLORS.space,
    color: COLORS.space75,
    labelColor: COLORS.space75,
    helper: COLORS.space75,
  },
};

export const FOLIAGE_PALETTE_COLORS: TPaletteColors = {
  text: COLORS.space60,
  themeCard: COLORS.space75,
  thumbnail: COLORS.space75,
  primary: COLORS.space60,
  secondary: COLORS.bleachedLinen,
  tertiary: COLORS.etonBlue,
  button: COLORS.etonBlue,
  buttonText: COLORS.space75,
  background: COLORS.white,
  backgroundAlt: COLORS.space08,
  input: {
    borderColor: COLORS.space40,
    borderColorActive: COLORS.etonBlue,
    borderColorHover: COLORS.space60,
    color: COLORS.space60,
    labelColor: COLORS.space60,
    helper: COLORS.space40,
  },
};

export const ROSES_PALETTE_COLORS: TPaletteColors = {
  text: COLORS.wenge,
  themeCard: COLORS.space,
  thumbnail: COLORS.space,
  primary: COLORS.wenge,
  secondary: COLORS.queenPink,
  tertiary: COLORS.white,
  button: COLORS.queenPink,
  buttonText: COLORS.wenge,
  background: COLORS.white,
  backgroundAlt: COLORS.space08,
  input: {
    borderColor: COLORS.space40,
    borderColorActive: COLORS.queenPink,
    borderColorHover: COLORS.space,
    color: COLORS.wenge,
    labelColor: COLORS.wenge,
    helper: COLORS.space40,
  },
};

export const WHIMSICAL_PALETTE_COLORS: TPaletteColors = {
  text: COLORS.solidPink,
  themeCard: COLORS.queenPinkWhimsy,
  thumbnail: COLORS.licorice,
  primary: COLORS.solidPink,
  secondary: COLORS.queenPinkWhimsy,
  tertiary: COLORS.pinkJoy,
  button: COLORS.pinkJoy,
  buttonText: COLORS.solidPink,
  background: COLORS.queenPinkWhimsy,
  backgroundAlt: COLORS.white,
  input: {
    borderColor: COLORS.solidPink,
    borderColorActive: COLORS.solidPink,
    borderColorHover: COLORS.space,
    color: COLORS.space,
    labelColor: COLORS.space,
    helper: COLORS.space,
  },
};

export const PALETTE_TEXT_COLORS: Record<Palettes, TPaletteColors['text']> = {
  [Palettes.Bridebook]: BRIDEBOOK_PALETTE_COLORS.text,
  [Palettes.Foliage]: FOLIAGE_PALETTE_COLORS.text,
  [Palettes.Roses]: ROSES_PALETTE_COLORS.text,
  [Palettes.Whimsical]: WHIMSICAL_PALETTE_COLORS.text,
};

export const PALETTE_THUMBNAIL_COLORS: Record<Palettes, TPaletteColors['thumbnail']> = {
  [Palettes.Bridebook]: BRIDEBOOK_PALETTE_COLORS.thumbnail,
  [Palettes.Foliage]: FOLIAGE_PALETTE_COLORS.thumbnail,
  [Palettes.Roses]: ROSES_PALETTE_COLORS.thumbnail,
  [Palettes.Whimsical]: WHIMSICAL_PALETTE_COLORS.thumbnail,
};

export const PALETTE_COLORS: Record<Palettes, TPaletteColors> = {
  [Palettes.Bridebook]: BRIDEBOOK_PALETTE_COLORS,
  [Palettes.Foliage]: FOLIAGE_PALETTE_COLORS,
  [Palettes.Roses]: ROSES_PALETTE_COLORS,
  [Palettes.Whimsical]: WHIMSICAL_PALETTE_COLORS,
};

export const PALETTE_HIGHLIGHT_COLORS: Record<Palettes, TColorPaletteHighlights> = {
  [Palettes.Bridebook]: [
    BRIDEBOOK_PALETTE_COLORS.primary,
    BRIDEBOOK_PALETTE_COLORS.secondary,
    BRIDEBOOK_PALETTE_COLORS.tertiary,
  ],
  [Palettes.Foliage]: [
    FOLIAGE_PALETTE_COLORS.primary,
    FOLIAGE_PALETTE_COLORS.secondary,
    FOLIAGE_PALETTE_COLORS.tertiary,
  ],
  [Palettes.Roses]: [
    ROSES_PALETTE_COLORS.primary,
    ROSES_PALETTE_COLORS.secondary,
    ROSES_PALETTE_COLORS.tertiary,
  ],
  [Palettes.Whimsical]: [
    WHIMSICAL_PALETTE_COLORS.primary,
    WHIMSICAL_PALETTE_COLORS.secondary,
    WHIMSICAL_PALETTE_COLORS.tertiary,
  ],
};

export const FONT_STYLES: Record<Fonts, IFontStyle> = {
  [Fonts.Playfair]: {
    fontUrl: 'https://fonts.googleapis.com/css2?family=Playfair+Display&display=swap',
    fontWeight: 700,
    fontFamily: fonts.playfair,
  },
  [Fonts.OpenSans]: {
    fontUrl: 'https://fonts.googleapis.com/css2?family=Open+Sans&display=swap',
    fontWeight: 400,
    fontFamily: fonts.default,
  },
  [Fonts.OrelegaOne]: {
    fontUrl: 'https://fonts.googleapis.com/css2?family=Orelega+One&display=swap',
    fontWeight: 400,
    fontFamily: 'Orelega One',
  },
  [Fonts.Montserrat]: {
    fontUrl: 'https://fonts.googleapis.com/css2?family=Montserrat&display=swap',
    fontWeight: 400,
    fontFamily: 'Montserrat',
  },
  [Fonts.Aboreto]: {
    fontUrl: 'https://fonts.googleapis.com/css2?family=Aboreto&display=swap',
    fontWeight: 400,
    fontFamily: 'Aboreto',
  },
  [Fonts.Figtree]: {
    fontUrl: 'https://fonts.googleapis.com/css2?family=Figtree&display=swap',
    fontWeight: 400,
    fontFamily: 'Figtree',
  },
  [Fonts.MeowScript]: {
    fontUrl: 'https://fonts.googleapis.com/css2?family=Meow+Script&display=swap',
    fontWeight: 400,
    fontFamily: 'Meow Script',
    lineHeight: '22px',
  },
  [Fonts.EBGaramond]: {
    fontUrl: 'https://fonts.googleapis.com/css2?family=EB+Garamond&display=swap',
    fontWeight: 400,
    fontFamily: 'EB Garamond',
  },
};

export const FONTS_PAIRS: Record<FontPairs, TFontsPair> = {
  [FontPairs.Bridebook]: {
    primary: Fonts.Playfair,
    secondary: Fonts.OpenSans,
  },
  [FontPairs.Foliage]: {
    primary: Fonts.OrelegaOne,
    secondary: Fonts.Montserrat,
  },
  [FontPairs.Roses]: {
    primary: Fonts.Aboreto,
    secondary: Fonts.Figtree,
  },
  [FontPairs.Whimsical]: {
    primary: Fonts.MeowScript,
    secondary: Fonts.EBGaramond,
  },
};
